import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
// Redux
import { useSelector } from "react-redux";
// Constants - Functions
import { pathologies, errorColor, bodyColor } from "../../../../config/constants";
import {
  renderAffectationsOptions,
  renderStrokeSideOptions,
  renderStrokeTypeOptions,
  renderStrokeLocationOptions,
  renderPareticSidesOptions,
  renderComorbidityOptions,
} from "../../../../config/dropdownFunctions";
import { isAISNProjectHospital } from "../../../../config/projectFunctions";
// Components
import { PathologyDiv, TitleTextDiv, DataDiv, ColumnDataDiv, InputDataDiv, Label, DateOfStrokeInput } from "./styles";
import Select from "../../../selectInput";
// Assets
import SelectArrow from "../../../../assets/icons/ico_arrow_down.svg";

const Pathology = ({
  pathology,
  setPathology,
  pathologyError,
  setPathologyError,
  strokeSide,
  setStrokeSide,
  strokeSideError,
  setStrokeSideError,
  strokeType,
  setStrokeType,
  strokeTypeError,
  setStrokeTypeError,
  strokeLocation,
  setStrokeLocation,
  strokeLocationError,
  setStrokeLocationError,
  dateOfStroke,
  setDateOfStroke,
  dateOfStrokeError,
  setDateOfStrokeError,
  hasHeminegligence,
  setHasHeminegligence,
  pareticSide,
  setPareticSide,
  comorbidity,
  setComorbidity,
  comorbidityError,
  setComorbidityError,
}) => {
  const [isAISNHospital, setIsAISNHospital] = useState(false);
  const hospitalName = useSelector(({ clinician }) => clinician.data.HOSPITAL_NAME);
  const [t] = useTranslation("global");

  // Set AISN project flag
  useEffect(() => {
    const handleSetAISNProject = async () => {
      const isAISNHospital = await isAISNProjectHospital(hospitalName);
      setIsAISNHospital(isAISNHospital);
    };
    handleSetAISNProject();

    return () => {
      setIsAISNHospital(false);
    };
  }, [hospitalName]);

  const handleSetInput = (e, setInput, setError) => {
    setInput(e.target.value);
    setError(false);
  };

  return (
    <PathologyDiv>
      <TitleTextDiv>
        <p>{t("pathology")}</p>
      </TitleTextDiv>
      <DataDiv>
        <ColumnDataDiv width="385px">
          <InputDataDiv>
            <Label fontWeight="bold">{t("pathology")} *</Label>
            <Select
              value={pathology}
              onChange={(e) => handleSetInput(e, setPathology, setPathologyError)}
              padding="5px 35px 5px 10px"
              width="200px"
              arrowImg={SelectArrow}
              backgroundPosition="175px center"
              error={pathologyError}
              disabled={isAISNHospital}
            >
              {renderAffectationsOptions(t)}
            </Select>
          </InputDataDiv>

          {isAISNHospital && (
            <>
              <InputDataDiv>
                <Label fontWeight="bold">{t("Stroke side")} *</Label>
                <Select
                  value={strokeSide}
                  onChange={(e) => handleSetInput(e, setStrokeSide, setStrokeSideError)}
                  padding="5px 35px 5px 10px"
                  width="200px"
                  arrowImg={SelectArrow}
                  backgroundPosition="175px center"
                  error={strokeSideError}
                >
                  {renderStrokeSideOptions(t)}
                </Select>
              </InputDataDiv>
              <InputDataDiv>
                <Label fontWeight="bold">{t("Stroke type")} *</Label>
                <Select
                  value={strokeType}
                  onChange={(e) => handleSetInput(e, setStrokeType, setStrokeTypeError)}
                  padding="5px 35px 5px 10px"
                  width="200px"
                  arrowImg={SelectArrow}
                  backgroundPosition="175px center"
                  error={strokeTypeError}
                >
                  {renderStrokeTypeOptions(t)}
                </Select>
              </InputDataDiv>
              <InputDataDiv>
                <Label fontWeight="bold">{t("Stroke location")} *</Label>
                <Select
                  value={strokeLocation}
                  onChange={(e) => handleSetInput(e, setStrokeLocation, setStrokeLocationError)}
                  padding="5px 35px 5px 10px"
                  width="200px"
                  arrowImg={SelectArrow}
                  backgroundPosition="175px center"
                  error={strokeLocationError}
                >
                  {renderStrokeLocationOptions(t)}
                </Select>
              </InputDataDiv>
            </>
          )}
          {Number(pathology) === pathologies.strokeId && (
            <InputDataDiv>
              <Label htmlFor="dateOfStroke" fontWeight="bold">
                {t("pathologyInformation_strokeDateLabel")} *
              </Label>
              <DateOfStrokeInput
                format="##/##/####"
                placeholder="dd/mm/yyyy"
                mask={["d", "d", "m", "m", "y", "y", "y", "y"]}
                value={dateOfStroke}
                onChange={(e) => handleSetInput(e, setDateOfStroke, setDateOfStrokeError)}
                border={dateOfStrokeError ? `1px solid ${errorColor}` : `1px solid ${bodyColor}`}
              />
            </InputDataDiv>
          )}
        </ColumnDataDiv>
        <ColumnDataDiv width="385px">
          {Number(pathology) === pathologies.strokeId && (
            <>
              <InputDataDiv activeAnimation>
                <Label fontWeight="bold">{t("pathologyInformation_heminegligenceLabel")} *</Label>
                <Select
                  value={hasHeminegligence}
                  onChange={(e) => setHasHeminegligence(e.target.value)}
                  padding="5px 35px 5px 10px"
                  width="200px"
                  arrowImg={SelectArrow}
                  backgroundPosition="175px center"
                >
                  <option value={0}>{t("no")}</option>
                  <option value={1}>{t("yes")}</option>
                </Select>
              </InputDataDiv>
              <InputDataDiv activeAnimation>
                <Label fontWeight="bold">{t("pathologyInformation_pareticSideLabel")} *</Label>
                <Select
                  value={pareticSide}
                  onChange={(e) => setPareticSide(e.target.value)}
                  padding="5px 35px 5px 10px"
                  width="200px"
                  arrowImg={SelectArrow}
                  backgroundPosition="175px center"
                >
                  {renderPareticSidesOptions(t)}
                </Select>
              </InputDataDiv>
              {isAISNHospital && (
                <InputDataDiv>
                  <Label fontWeight="bold">{t("Comorbidity")} *</Label>
                  <Select
                    value={comorbidity}
                    onChange={(e) => handleSetInput(e, setComorbidity, setComorbidityError)}
                    padding="5px 35px 5px 10px"
                    width="200px"
                    arrowImg={SelectArrow}
                    backgroundPosition="175px center"
                    error={comorbidityError}
                  >
                    {renderComorbidityOptions(t)}
                  </Select>
                </InputDataDiv>
              )}
            </>
          )}
        </ColumnDataDiv>
      </DataDiv>
    </PathologyDiv>
  );
};

export default Pathology;
